import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '@/lib/api';
import api from '@/lib/api';

const AuthContext = createContext(null);

// Token refresh interval (6 hours)
const TOKEN_REFRESH_INTERVAL = 6 * 60 * 60 * 1000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  // Function to refresh token
  const refreshToken = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return false;
      
      const res = await api.post('/auth/refresh');
      if (res.data?.access_token) {
        localStorage.setItem('token', res.data.access_token);
        console.log('Token refreshed successfully');
        return true;
      }
      return false;
    } catch (err) {
      console.log('Token refresh failed:', err.message);
      return false;
    }
  }, []);

  // Validate user session
  const validateSession = useCallback(async () => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (!token || !savedUser) {
      setUser(null);
      setLoading(false);
      setInitialized(true);
      return;
    }

    // Set user from localStorage immediately for better UX
    const parsedUser = JSON.parse(savedUser);
    setUser(parsedUser);
    
    try {
      // Verify token is still valid
      const res = await authAPI.getMe();
      setUser(res.data);
      localStorage.setItem('user', JSON.stringify(res.data));
    } catch (err) {
      console.log('Session validation failed:', err.message);
      
      // If 401, try to refresh token first
      if (err.response?.status === 401) {
        const refreshed = await refreshToken();
        if (refreshed) {
          // Retry getMe after refresh
          try {
            const res = await authAPI.getMe();
            setUser(res.data);
            localStorage.setItem('user', JSON.stringify(res.data));
          } catch {
            // Still failed, logout
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setUser(null);
          }
        } else {
          // Refresh failed, logout
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
        }
      }
      // For other errors (network, server error), keep user logged in with local data
      // to prevent accidental logouts due to temporary issues
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  }, [refreshToken]);

  useEffect(() => {
    validateSession();
  }, [validateSession]);

  // Set up token refresh interval when user is logged in
  useEffect(() => {
    if (!user || !initialized) return;
    
    // Refresh token periodically to keep session alive
    const intervalId = setInterval(refreshToken, TOKEN_REFRESH_INTERVAL);
    
    // Also refresh on user activity (page visibility change)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshToken();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, initialized, refreshToken]);

  const login = async (email, password) => {
    const res = await authAPI.login({ email, password });
    const { access_token, user: userData } = res.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const register = async (name, email, password, role = 'student', phone = '', school = '') => {
    const res = await authAPI.register({ name, email, password, role, phone, school });
    const { access_token, user: userData } = res.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const isAdmin = user?.role === 'admin';

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, isAdmin, refreshToken }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
